import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  CHIP_GAP_PX,
  getLabelLayout,
  MAX_LABEL_LENGTH,
  measureChipWidth,
} from './drive-label-chips-cell.utils';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface GroupChipsCellProps {
  groupEmails?: string[];
}

interface GroupChipLayout {
  readonly visibleCount: number;
  readonly truncatedLabelMaxWidth?: number;
}

const fullChipSx = {
  maxWidth: `${MAX_LABEL_LENGTH}ch`,
  flexShrink: 0,
} as const;

const truncatableChipSx = {
  minWidth: 0,
  flexShrink: 0,
  maxWidth: `min(100%, ${MAX_LABEL_LENGTH}ch)`,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
} as const;

const countChipSx = {
  flexShrink: 0,
} as const;

// The overflow tooltip shows at most this many hidden groups at once; the rest scroll. A small
// MUI Chip is 24px tall and the list gap below is 4px (gap={0.5}).
const TOOLTIP_MAX_VISIBLE_CHIPS = 5;
const TOOLTIP_CHIP_HEIGHT_PX = 24;
const TOOLTIP_CHIP_GAP_PX = 4;
const TOOLTIP_LIST_MAX_HEIGHT_PX =
  TOOLTIP_MAX_VISIBLE_CHIPS * TOOLTIP_CHIP_HEIGHT_PX +
  (TOOLTIP_MAX_VISIBLE_CHIPS - 1) * TOOLTIP_CHIP_GAP_PX;

// Matches DriveLabelChipsCell's tooltip treatment exactly (per design: "stay consistent and
// follow the same format we applied for labels on Drive Explorer and Search") - MUI's default
// tooltip background is a small dark scrim meant for a line of text, not a wrapped row of chips,
// so it's overridden to a proper themed surface with a shadow.
const tooltipSlotProps = {
  tooltip: {
    sx: {
      maxWidth: 320,
      bgcolor: 'background.paper',
      color: 'text.primary',
      boxShadow: 1,
      p: 1,
    },
  },
  arrow: {
    sx: {
      color: 'background.paper',
    },
  },
} as const;

export default function GroupChipsCell({ groupEmails }: GroupChipsCellProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const emails = groupEmails ?? [];
  const [layout, setLayout] = useState<GroupChipLayout>({
    visibleCount: emails.length,
  });

  const emailsKey = emails.join('\u0000');

  const updateLayout = useCallback(() => {
    const container = containerRef.current;
    if (!container || !emails.length) {
      return;
    }

    const containerWidth = container.clientWidth;
    if (containerWidth <= 0) {
      return;
    }

    const chipWidths = emails.map((email) =>
      measureChipWidth(email, MAX_LABEL_LENGTH)
    );

    const countChipWidths = new Map<number, number>();
    for (let count = 1; count < emails.length; count++) {
      countChipWidths.set(count, measureChipWidth(`+${count}`));
    }

    setLayout((prev) => {
      const next = getLabelLayout(
        chipWidths,
        containerWidth,
        countChipWidths,
        CHIP_GAP_PX
      );

      if (
        prev.visibleCount === next.visibleCount &&
        prev.truncatedLabelMaxWidth === next.truncatedLabelMaxWidth
      ) {
        return prev;
      }

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- emailsKey is a stable proxy for emails' content
  }, [emailsKey]);

  useLayoutEffect(() => {
    updateLayout();

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateLayout]);

  if (emails.length === 0) {
    return <Typography variant="body2">-</Typography>;
  }

  const { visibleCount, truncatedLabelMaxWidth } = layout;
  const hiddenCount = emails.length - visibleCount;
  const hasOverflow = hiddenCount > 0;
  const useTruncatableChip = visibleCount === 1;

  return (
    <Stack
      ref={containerRef}
      flexDirection="row"
      alignItems="center"
      gap={1}
      height="100%"
      width="100%"
      sx={{ minWidth: 0, overflow: 'hidden' }}
    >
      {emails.slice(0, visibleCount).map((email) => (
        <Chip
          key={email}
          label={email}
          size="small"
          sx={
            useTruncatableChip
              ? {
                  ...truncatableChipSx,
                  ...(hasOverflow && truncatedLabelMaxWidth !== undefined
                    ? { maxWidth: truncatedLabelMaxWidth }
                    : {}),
                }
              : fullChipSx
          }
        />
      ))}
      {hasOverflow && (
        <Tooltip
          title={
            <Stack
              data-testid="group-chips-overflow-list"
              gap={0.5}
              flexDirection="column"
              alignItems="flex-start"
              sx={{
                maxHeight: TOOLTIP_LIST_MAX_HEIGHT_PX,
                overflowY: 'auto',
                // Don't hand the scroll to the grid/page behind once the list hits its end.
                overscrollBehavior: 'contain',
              }}
            >
              {emails.slice(visibleCount).map((email) => (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  sx={{ maxWidth: '100%' }}
                />
              ))}
            </Stack>
          }
          placement="top-start"
          arrow
          // Gives the pointer time to travel from the "+N" chip into the (scrollable) list
          // without the tooltip closing underneath it.
          leaveDelay={250}
          slotProps={tooltipSlotProps}
        >
          <Chip label={`+${hiddenCount}`} size="small" sx={countChipSx} />
        </Tooltip>
      )}
    </Stack>
  );
}
